import { graphql, HeadProps, PageProps } from 'gatsby';
import React from 'react';
import ReactDOM from 'react-dom';
import { SEO } from '@zempler/theme/src/components/Seo';
import { HelpCenterHero } from '@zempler/theme/src/components/helpCenterHero/HelpCenterHero';
import { HelpCommonQuestions } from '@zempler/theme/src/components/helpCommonQuestions/HelpCommonQuestions';
import { HelpTopics } from '@zempler/theme/src/components/helpTopics/HelpTopics';
import { HelpTopicArticles } from '@zempler/theme/src/components/helpTopicArticles/HelpTopicArticles';
import { Section } from '@zempler/theme/src/components/section/Section';
import { TitleBlock } from '@zempler/theme/src/components/titleBlock/TitleBlock';
import Hero from '@zempler/theme/src/components/hero/Hero';
import { PageComponentRenderer } from '../components/componentRenderer/PageComponentRenderer';

if (process.env.NODE_ENV !== 'production') {
  const axe = require('@axe-core/react');
  axe(React, ReactDOM, 1000);
}

const HelpCentreTopic = ({ data }: PageProps<Queries.UmbracoPageQuery>) => {
  const { url, name, leadText, parentPage, childPages } = data?.page;
  const { pageComponents, heading } = data?.page?.properties;
  const pageComponentsData = pageComponents?.[0]?.block?.components;
  const topics = data?.topics?.nodes.sort((a: any, b: any) => a.sortOrder - b.sortOrder);

  const helpTopicsCategories = childPages?.sort((a: any, b: any) => a.sortOrder - b.sortOrder)?.map((childPage) => {
    const { name, childPages } = childPage;

    // order the child pages using sortOrder

    const articles = childPages.sort((a: any, b: any) => a.sortOrder - b.sortOrder)?.map((childPage) => ({
      name: childPage?.name,
      url: childPage?.url,
    }));

    return {
      name,
      articles,
    };
  });

  return (
    <>
      {/* Component: Hero  */}
      <Hero
        superTitle={name}
        heading={heading}
        subHeading={leadText}
        backgroundColor="neutral"
        breadcrumb={[{ ...parentPage }]}
      />
      {/* Page components start below - Data source: Page components on page  */}

      {/* Component: Section - Articles */}
      <Section backgroundColor="white">
        <HelpTopicArticles
          helpTopics={topics}
          helpTopicCategories={helpTopicsCategories}
          currentUrl={url}
        />
      </Section>

      {/* Component: Section - TOPICS */}
      <Section backgroundColor="darkBlue">
        {/* Component: TitleBlock */}
        <TitleBlock heading="Explore all help topics" />

        {/* Component: Help Topics */}
        <HelpTopics helpTopics={topics} />
      </Section>

      <PageComponentRenderer components={pageComponentsData} />
    </>
  );
};

export default HelpCentreTopic;

export const Head = ({ data }: HeadProps<Queries.UmbracoPageQuery>) => {
  const seo = data.page?.properties?.seo;
  const feedImg = data.page?.properties?.feed?.featuredImage?.image?.localFile?.ogImg?.src;
  return <SEO data={seo} image={feedImg ? `${data.site.siteMetadata.siteUrl}${feedImg}` : undefined } />;
};

export const umbracoHelpCentreTopicQuery = graphql`
  query UmbracoHelpCentreTopic($id: String!) {
    site {
        siteMetadata {
          siteUrl
        }
    }
    page: umbracoHelpCentreTopicType(id: { eq: $id }) {
      name
      remoteId
      url
      name
      leadText
      parentPage {
        label: name
        url
      }
      childPages {
        name
        url
        sortOrder
        ... on UMBRACO_HelpCentreTopicCategoryType {
          name
          childPages {
            url
            name
            sortOrder
          }
        }
      }
      properties {
        heading
        ...PageComponents
        seo {
          ...SEO
        }
        feed {
          featuredImage {
            image {
              ...OgImage
            }
          }
        }
      }
    }
    topics: allUmbracoHelpCentreTopicType {
      nodes {
        name
        url
        sortOrder
        description: shortDescription
        backgroundColor: backgroundColour
      }
    }
  }
`;
